import type { Rail, Vec2 } from "../level";
import { checkColor, rad, withColor, type PieceKind, type Placed, type PrimKind } from "./types";

/** A flat-topped block `h` tall, ±hu along and ±hw across. */
export interface BoxPrim extends Placed {
  kind: "box";
  hu: number;
  h: number;
  /** Render only (#115): its own color, and what it's drawn as. */
  color?: string;
  deco?: BoxDeco;
}

/**
 * What a box is drawn as besides a plain block (#115), render only: the
 * sim sees the same block. `planter` has soil and shrubs on top, `tree` a
 * street tree growing out of it, and `lamp` is a street lamp on a concrete
 * plinth (a tall box, any size: the sim handles a post however thin, #124).
 * `table` and `bench` (#167) are a picnic table's top and benches: a wooden
 * slab on legs, the box's top; lay a bench box against each long side of a
 * table box. `cab` (#246) is a crashed taxi sunk under plywood, its roof
 * the box's top and sheets thrown over its hood and trunk at that height;
 * lean a kicker against each long side for the hop over it.
 */
export type BoxDeco = "planter" | "tree" | "lamp" | "table" | "bench" | "cab";
export const BOX_DECOS: readonly BoxDeco[] = ["planter", "tree", "lamp", "table", "bench", "cab"];

export type LedgeSide = "u+" | "u-" | "w+" | "w-";
export const LEDGE_SIDES: readonly LedgeSide[] = ["u+", "u-", "w+", "w-"];

/** A flat-topped block. `ledges` puts grindable edges on those sides. */
export interface BoxPiece {
  kind: "box";
  x: number;
  z: number;
  rot?: number;
  hu: number;
  hw: number;
  h: number;
  ledges?: LedgeSide[];
  /** Its own color (#115), over the map's `look.pieces`. */
  color?: string;
  deco?: BoxDeco;
}

export const BOX: PrimKind<BoxPrim> = {
  eval: (p, u, w) => (Math.abs(u) <= p.hu && Math.abs(w) <= p.hw ? { h: p.h, du: 0, dw: 0 } : null),
  extent: (p) => [-p.hu, p.hu],
  shadow: 0.55,
  top: (p) => p.h,
  plan: (p) => [{ u0: -p.hu, u1: p.hu, fill: "box" }],
};

/** Grindable edges along the given sides of a box. */
export function boxLedges(b: BoxPrim, sides: LedgeSide[]): Rail[] {
  const c = Math.cos(b.rot);
  const s = Math.sin(b.rot);
  const toWorld = (u: number, w: number): Vec2 => ({ x: b.x + u * c - w * s, z: b.z + u * s + w * c });
  return sides.map((side) => {
    const [a, bb] =
      side === "u+"
        ? [toWorld(b.hu, -b.hw), toWorld(b.hu, b.hw)]
        : side === "u-"
          ? [toWorld(-b.hu, -b.hw), toWorld(-b.hu, b.hw)]
          : side === "w+"
            ? [toWorld(-b.hu, b.hw), toWorld(b.hu, b.hw)]
            : [toWorld(-b.hu, -b.hw), toWorld(b.hu, -b.hw)];
    return { a, b: bb, y: b.h, kind: "ledge" as const };
  });
}

export const BOX_PIECE: PieceKind<BoxPiece> = {
  fields: ["x", "z", "rot", "hu", "hw", "h", "ledges", "color", "deco"],
  placed: true,
  check(c, o, path, bounds) {
    c.num(o, "hu", path, { min: 0.05, max: 2 * bounds });
    c.num(o, "h", path, { min: 0.05, max: 20 });
    if (o.ledges !== undefined && (!Array.isArray(o.ledges) || !o.ledges.every((s) => LEDGE_SIDES.includes(s as LedgeSide))))
      c.fail(`${path}.ledges`, `expected a list of sides from ${LEDGE_SIDES.join(", ")}`);
    checkColor(c, o, path);
    if (o.deco !== undefined && !BOX_DECOS.includes(o.deco as BoxDeco)) c.fail(`${path}.deco`, `expected one of ${BOX_DECOS.join(", ")}`);
  },
  compile(p, out) {
    const plain: BoxPrim = { kind: "box", x: p.x, z: p.z, rot: rad(p.rot), hu: p.hu, hw: p.hw, h: p.h };
    const box = withColor(p.deco === undefined ? plain : { ...plain, deco: p.deco }, p.color);
    out.prims.push(box);
    if (p.ledges?.length) out.rails.push(...boxLedges(box, p.ledges));
  },
};
